import * as _ from 'lodash-es'
import { copy } from 'fast-copy'
import axios, * as others from 'axios'

import InvoiceGenerator from './invoiceGenerator'

const invoiceGenerator = new InvoiceGenerator()

const statusDisplay = {
  inprogress: 'In Progress',
  quoting: 'Quoting',
  invoiced: 'Invoiced',
}

const state = {
  job: undefined,
  uninvoicedJobs: undefined,
  rateCard: undefined,
  talentTypes: undefined,
  quoteTemplate: undefined,
}

const mutations = {
  SET_JOB(state, job) {
    state.job = job
  },
  SET_JOB_TALENT_TYPES(state, talentTypes) {
    state.job.quote.talentTypes = talentTypes
  },
  SET_QUOTE_SECTION(state, section) {
    const sectionIndex = state.job.quote.sections.findIndex(x => x._id == section._id)
    state.job.quote.sections[sectionIndex] = section
  },
  SET_UNINVOICED_JOBS(state, jobs) {
    state.uninvoicedJobs = jobs
  },
  SET_RATE_CARD(state, rateCard) {
    state.rateCard = rateCard
  },
  SET_TALENT_TYPES(state, talentTypes) {
    state.talentTypes = talentTypes
  },
  SET_QUOTE_TEMPLATE(state, quoteTemplate) {
    state.quoteTemplate = quoteTemplate
  },
}

const updateQuote = (state, commit) => {
  const jobToUpdate = copy(state.job)
  const quote = jobToUpdate.quote

  quote.totalQuoted = quote.sections.reduce((acc, section) => {
    return acc + section.totalQuoted || 0
  }, 0)
  quote.totalExpenses = quote.sections.reduce((acc, section) => {
    return acc + section.totalCost
  }, 0)
  quote.totalSuper = quote.sections.reduce((acc, section) => {
    return acc + section.totalSuper
  }, 0)
  quote.totalSandbox = quote.sections.reduce((acc, section) => {
    const talentVal = section.talent.reduce((acc2, talent) => {
      return talent.type.usesRateCard ? acc2 + talent.totalCost : acc2
    }, 0)
    return acc + talentVal
  }, 0)
  quote.productionMargin = quote.totalQuoted - (quote.totalExpenses + quote.totalSuper)
  quote.gst = Math.round((quote.totalQuoted * 0.1 + Number.EPSILON) * 100) / 100
  quote.grandTotal = quote.totalQuoted + quote.gst
  commit('SET_JOB', jobToUpdate)
}

const setTalentTypesFromDatabase = async (commit, state, rootState) => {
  if (!state.talentTypes) {
    const response = await axios.get(
      '/api/quote-template/talent-types/' + rootState.auth.user.attributes['custom:customer']
    )
    commit('SET_TALENT_TYPES', response.data.talentTypes)
  }
}

const setRateCardFromDatabase = async (commit, state, rootState) => {
  if (!state.rateCard) {
    const response = await axios.get(
      '/api/quote-template/rate-card/' + rootState.auth.user.attributes['custom:customer']
    )
    for (const rate of response.data.rateCard) {
      rate.value = { text: rate.text, cost: rate.cost, markup: rate.markup }
    }
    commit(
      'SET_RATE_CARD',
      response.data.rateCard.sort((a, b) => {
        return a.text.localeCompare(b.text, 'en', { sensitivity: 'base' })
      })
    )
  }
}

const actions = {
  async setJobForQuote({ commit, dispatch, state, rootState }, job) {
    if (!job) {
      let job = undefined
      if (localStorage['ls.currentJob']) {
        const response = await axios.get('/api/job/id/' + JSON.parse(localStorage['ls.currentJob']).id)
        job = response.data.job
        job.quote = response.data.job.quote || copy(state.quoteTemplate)
      }
      if (!job.quote.quoteTitle) {
        job.quote.quoteTitle = job.title
      }
      commit('SET_JOB', job)
    } else {
      if (!job.quote) {
        if (!state.quoteTemplate) {
          await dispatch('setQuoteTemplate')
        }
        job.quote = copy(state.quoteTemplate)
      }
      job.quote.quoteTitle = job.title
      commit('SET_JOB', job)
    }

    if (!state.talentTypes) {
      await setTalentTypesFromDatabase(commit, state, rootState)
    }

    if (!state.job.quote.talentTypes) {
      commit('SET_JOB_TALENT_TYPES', state.talentTypes)
    } else {
      // add any new talent types
      for (const talentType of state.talentTypes) {
        const exists = state.job.quote.talentTypes.find(tt => {
          return tt._id === talentType._id
        })
        if (!exists) {
          state.job.quote.talentTypes.push(talentType)
        }
      }
    }
  },
  setJob({ commit }, job) {
    commit('SET_JOB', job)
  },
  clearJob({ commit }) {
    commit('SET_JOB', undefined)
  },
  async setQuoteTemplate({ commit, state, rootState }) {
    if (!state.quoteTemplate) {
      const response = await axios.get('/api/quote-template/' + rootState.auth.user.attributes['custom:customer'])
      commit('SET_QUOTE_TEMPLATE', response.data.quoteTemplate[0])
    }
  },
  async getUninvoicedJobs({ commit }) {
    try {
      const response = await axios.get('/api/job/uninvoiced')
      // statusDisplay is set for every job, not just the ones missing producerComplete: a job
      // that already has producerComplete stored used to skip this and render a blank Status.
      const uninvoicedJobs = response.data.job.map(job => {
        if (job.producerComplete === undefined) {
          job.producerComplete = false
        }
        job.statusDisplay = statusDisplay[job.status]
        return job
      })
      commit('SET_UNINVOICED_JOBS', uninvoicedJobs)
    } catch (err) {
      console.log('Error getting uninvoiced', err)
    }
  },
  setProducerComplete({ commit, state }, producerValues) {
    const updatedJob = _.find(state.uninvoicedJobs, { jobNumber: producerValues.jobNumber })
    updatedJob.producerComplete = producerValues.value
    commit('SET_UNINVOICED_JOBS', state.uninvoicedJobs)
  },
  // Mirrors setProducerComplete. The invoice toggle is a controlled button, so the store has to
  // carry the new status or the label never leaves "UnInvoiced".
  setJobStatus({ commit, state }, statusValues) {
    const updatedJob = _.find(state.uninvoicedJobs, { jobNumber: statusValues.jobNumber })
    updatedJob.status = statusValues.value
    updatedJob.statusDisplay = statusDisplay[statusValues.value]
    commit('SET_UNINVOICED_JOBS', state.uninvoicedJobs)
  },
  updateQuoteSection({ commit, state }, section) {
    if (state.talentTypes && state.job.quote.talentTypes) {
      section.talent.map(talent => {
        talent.quantityDisplayType = talent.type.usesQuantities ? talent.quantityType : ''
        talent.usesRateCard = talent.type.usesRateCard
        talent.usesQuantities = talent.type.usesQuantities

        if (talent.type.usesRateCard && talent.rateCardSelection) {
          talent.cost = talent.rateCardSelection.cost
          if (talent.customMarkupValue) {
            talent.markup = talent.customMarkupValue
          } else {
            talent.markup = talent.cost + talent.rateCardSelection.markup
          }
        } else if (talent.type.usesRateCard && !talent.rateCardSelection) {
          talent.markup = 0
        } else {
          if (talent.customMarkupValue) {
            talent.markup = talent.customMarkupValue
          } else {
            const thisTalentType = state.job.quote.talentTypes.find(tt => {
              return tt._id === talent.type._id
            })

            if (!thisTalentType) {
              throw Error('Invalid talent type: ' + talent.type.text)
            }

            talent.markup = talent.cost ? talent.cost * (1 + thisTalentType.markup / 100) : 0
          }
        }

        if (talent.type.usesQuantities) {
          talent.totalCost = talent.cost * talent.quantity * talent.days
          talent.quoted = talent.markup * talent.quantity * talent.days
        } else {
          talent.totalCost = talent.cost * talent.quantity
          talent.quoted = talent.markup * talent.quantity
        }

        talent.totalCost = talent.totalCost || 0
        talent.super = talent.type.applyTax ? (talent.super = talent.totalCost * 0.095) : 0
        talent.quoted = talent.quoted || 0
      })
      section.totalCost = section.talent.reduce((acc, talent) => {
        return acc + talent.totalCost
      }, 0)
      section.totalSuper = section.talent.reduce((acc, talent) => {
        return acc + talent.super
      }, 0)
      section.totalQuoted = section.talent.reduce((acc, talent) => {
        return acc + talent.quoted
      }, 0)
      commit('SET_QUOTE_SECTION', section)

      for (const section of state.job.quote.sections) {
        for (const talent of section.talent) {
          if (!talent.rateCardSelection) {
            const rcItem = state.rateCard.find(rc => {
              return rc.text === talent.talentName
            })
            talent.rateCardSelection = rcItem ? rcItem.value : undefined
          }
        }
      }
      updateQuote(state, commit)
    }
  },
  addTalentLine({ commit, state }, section) {
    section.talent.push({ quantityType: 'days', quantityDisplayType: 'days', isAdditionalRow: true })
    commit('SET_QUOTE_SECTION', section)
  },
  async updateQuoteSections({ commit, dispatch, state, rootState, getters }) {
    await setTalentTypesFromDatabase(commit, state, rootState)
    await setRateCardFromDatabase(commit, state, rootState)
    for (const section of state.job.quote.sections) {
      dispatch('updateQuoteSection', section)
    }
  },
  generateQuotePdf({ state }, preview) {
    invoiceGenerator.generate(state.job, preview)
  },
  async setTalentTypes({ commit, state, rootState }) {
    await setTalentTypesFromDatabase(commit, state, rootState)
  },
  async saveTalentTypes({ commit, state }) {
    const response = await axios.post('/api/quote-template/talent-types', state.talentTypes)
    const strippedTalentTypes = response.data.talentTypes.filter(tt => {
      return !tt.deleted
    })

    commit('SET_TALENT_TYPES', strippedTalentTypes)
    return response
  },
  addTalentType({ state, commit, rootState }) {
    const newTalentTypes = copy(state.talentTypes)
    const newTalentType = { customer: rootState.auth.user.attributes['custom:customer'] }
    newTalentTypes.push(newTalentType)
    commit('SET_TALENT_TYPES', newTalentTypes)
  },
  deleteTalentType({ state, commit }, talentType) {
    const talentTypes = copy(state.talentTypes)
    const thisTalentType = talentTypes.find(tt => {
      return tt._id === talentType._id
    })
    thisTalentType.deleted = true
    commit('SET_TALENT_TYPES', talentTypes)
  },

  async setRateCard({ commit, state, rootState }) {
    await setRateCardFromDatabase(commit, state, rootState)
  },
  async saveRateCard({ commit, state }) {
    const response = await axios.post('/api/quote-template/rate-card', state.rateCard)
    const strippedRateCard = response.data.rateCard.filter(rc => {
      return !rc.deleted
    })

    commit('SET_RATE_CARD', strippedRateCard)
    return response
  },
  addRateCardItem({ state, commit, rootState }) {
    const newRateCardItems = copy(state.rateCard)
    const newRateCardItem = { customer: rootState.auth.user.attributes['custom:customer'] }
    newRateCardItems.push(newRateCardItem)
    commit('SET_RATE_CARD', newRateCardItems)
  },
  deleteRateCardItem({ state, commit }, rateCardItem) {
    const rateCard = copy(state.rateCard)
    const thisRateCardItem = rateCard.find(rc => {
      return rc._id === rateCardItem._id
    })
    thisRateCardItem.deleted = true
    commit('SET_RATE_CARD', rateCard)
  },

  // The template's own document. These three were empty stubs - the commented-out bodies were the
  // rate card's, copy-pasted and never written - so the sections tab could not save anything (#660).
  async saveQuoteTemplate({ commit, state }) {
    const response = await axios.post('/api/quote-template', state.quoteTemplate)
    const saved = (response.data.quoteTemplate || [])[0]

    if (saved) {
      commit('SET_QUOTE_TEMPLATE', saved)
    }

    return response
  },
  addQuoteSection({ state, commit, rootState }) {
    const template = copy(state.quoteTemplate) || {}
    template.sections = template.sections || []
    template.customer = template.customer || rootState.auth.user.attributes['custom:customer']
    template.sections.push({ sectionTitle: '', talent: [] })
    commit('SET_QUOTE_TEMPLATE', template)
  },
  // Marked rather than removed, matching the talent types and the rate card above: the getter
  // filters `deleted`, and the save then tells the server which rows are gone.
  deleteQuoteSection({ state, commit }, section) {
    const template = copy(state.quoteTemplate)

    if (!template || !template.sections) {
      return
    }

    const thisSection = template.sections.find(candidate => candidate._id === section._id)

    if (thisSection) {
      thisSection.deleted = true
    }

    commit('SET_QUOTE_TEMPLATE', template)
  },

  async deleteQuote({ state, commit }, job) {
    try {
      const jobMinusQuote = await axios.delete('/api/job/quote/' + job.quote._id)
      commit('SET_JOB', jobMinusQuote.data)
    } catch (err) {
      console.log('Error deleting quote', err)
    }
  },

  async setJobByBooking({ commit }, booking) {
    const httpResult = await axios.get('/api/job/booking/' + booking.sequence)
    if (httpResult.data.job) {
      commit('SET_JOB', httpResult.data.job)
    }
  },

  async save({ rootGetters, state }, bookingToSave) {
    const currentUser = rootGetters['auth/getCurrentUser']
    const httpResult = await axios.post('/api/job', {
      job: state.job,
      booking: bookingToSave,
      username: currentUser.attributes.name,
    })

    //   if (httpResult.data.job) {
    //     jobService.setCurrentJob(httpResult.data.job)
    //     AlertService.add('success', prefix + currentJob.jobNumber + ' saved')
    //   }

    // .catch(error => {
    //   console.log(error)
    //   AlertService.add('error', 'There was a problem saving the job')
    // })
  },
}

const getters = {
  currentJob: state => {
    return state.job
  },
  uninvoicedJobs: state => {
    return _.groupBy(state.uninvoicedJobs, job => {
      return job.producer
    })
  },
  talentTypeOptions: state => {
    return state.talentTypes
      ? state.talentTypes.map(t => {
          return { text: t.text, value: t }
        })
      : []
  },
  talentTypes: state => {
    if (!state.talentTypes) {
      return []
    } else {
      return state.talentTypes.filter(tt => {
        return !tt.deleted
      })
    }
  },
  rateCard: state => {
    if (!state.rateCard) {
      return []
    } else {
      return state.rateCard.filter(rc => {
        return !rc.deleted
      })
    }
  },
  quoteTemplate: state => {
    if (!state.quoteTemplate) {
      return {}
    } else {
      if (state.quoteTemplate.sections) {
        return state.quoteTemplate.sections.filter(qt => {
          return !qt.deleted
        })
      }
      return state.quoteTemplate
    }
  },
}

export default {
  namespaced: true,
  state: state,
  mutations: mutations,
  actions: actions,
  getters: getters,
}
